import { BOT_SEQUENCE_STANDARD_DAYS, BotSequenceConfigEffectiveField, BotSequenceConfigStep, BotSequenceType } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";
import { useState } from "react";

import { CortexInheritancePill } from "../../CortexV3/Components/CortexInheritancePill";
import { FieldState } from "../Hooks/useSequenceConfig";
import { AddDayControl } from "../Ladder/AddDayControl";
import { DayRow } from "../Ladder/DayRow";
import { DayCapabilityNotes, DaySettings } from "../Ladder/DaySettings";
import { addDay, copyLadderForScope, removeDay, savedStepKeys, seedStandardDays, sortLadder, updateDay } from "../ladderDraft";
import { SequenceScopeKind, scopeNoun } from "../sequenceScope";
import { inheritanceFor } from "./ModeSection";

interface LadderSectionProps {
  sequenceType: BotSequenceType;
  effective: BotSequenceConfigEffectiveField<BotSequenceConfigStep[] | null>;
  staged: FieldState<BotSequenceConfigStep[]>;
  scopeKind: SequenceScopeKind;
  canEdit: boolean;
  capability: DayCapabilityNotes;
  onStage: (ladder: BotSequenceConfigStep[]) => void;
  onRevert: () => void;
  onDiscard: () => void;
}

/**
 * The days of the sequence. The ladder cascades as ONE unit: inherited rows are read-only
 * until "Customize" copies them here; an own ladder edits in place; "Revert to inherited"
 * stages `null`. A saved day's number is fixed (decision 37).
 */
export function LadderSection({
  sequenceType,
  effective,
  staged,
  scopeKind,
  canEdit,
  capability,
  onStage,
  onRevert,
  onDiscard
}: LadderSectionProps): React.ReactElement {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const ownsHere = effective.source === scopeKind;
  const editing = staged.kind === "set";
  const ladder: BotSequenceConfigStep[] = editing ? staged.value : staged.kind === "revert" ? [] : (effective.value ?? []);
  const isEditable = canEdit && (editing || ownsHere);
  const savedKeys = savedStepKeys(effective.value);
  const noun = scopeNoun(scopeKind);
  const [addError, setAddError] = useState<string | null>(null);

  const stage = (next: BotSequenceConfigStep[]): void => onStage(sortLadder(next));

  const newDayOptions = { fmsNoteAllowed: capability.fmsNoteDisabledReason === null, emailAllowed: capability.emailDisabledReason === null };

  // Customizing an empty sequence gives it the standard days, fully filled in (R4);
  // customizing inherited days copies them.
  const handleCustomize = (): void => {
    const inherited = effective.value ?? [];
    onStage(inherited.length === 0 ? seedStandardDays(sequenceType, newDayOptions) : copyLadderForScope(inherited));
  };

  const handleSeedStandardDays = (): void => {
    stage(seedStandardDays(sequenceType, newDayOptions));
  };

  const handleRevert = (): void => {
    const message =
      scopeKind === "org"
        ? "Remove all days? With no days at the organization this sequence becomes Not configured everywhere that inherits it."
        : `Revert to inherited? This removes this ${noun}'s days; the ${scopeKind === "location" ? "group or organization" : "organization"}'s days apply again.`;
    if (!window.confirm(message)) return;
    if (editing && !ownsHere) onDiscard();
    else onRevert();
  };

  const handleAdd = (offsetDays: number): void => {
    const result = addDay(ladder, offsetDays, sequenceType, newDayOptions);
    if (!result.ok) {
      setAddError(result.error);
      return;
    }
    setAddError(null);
    onStage(result.ladder);
    setExpandedKey(result.ladder.find((step) => step.offsetDays === offsetDays)?.key ?? null);
  };

  const inheritedBanner = !ownsHere && !editing && canEdit;
  const empty = ladder.length === 0 && staged.kind !== "revert";

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="heading-base-alt text-grey-900">Days</h3>
            <CortexInheritancePill inheritance={editing ? inheritanceFor(scopeKind, scopeKind) : inheritanceFor(effective.source, scopeKind)} />
          </div>
          <p className="body-sm mt-0.5 text-grey-600">
            {isEditable
              ? "Days are inherited as one set. A saved day's number cannot be changed; add or remove days instead."
              : inheritedBanner
                ? `Read-only until you customize. Customizing copies every day here; from then on changes made above no longer reach this ${noun} until you revert.`
                : "Days are inherited as one set."}
          </p>
        </div>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-2">
            {inheritedBanner ? (
              <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} onClick={handleCustomize}>
                {empty ? (scopeKind === "org" ? "Set up days" : "Customize") : `Customize for this ${noun}`}
              </Button>
            ) : (
              (ownsHere || editing) && (
                <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} onClick={handleRevert}>
                  {scopeKind === "org" ? "Remove all days" : "Revert to inherited"}
                </Button>
              )
            )}
          </div>
        )}
      </div>

      <div className="border-t border-grey-200">
        {staged.kind === "revert" ? (
          <p className="body-sm px-4 py-3 text-grey-600">Reverts to the inherited days when you save.</p>
        ) : ladder.length === 0 ? (
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <p className="body-sm text-grey-600">No days yet.</p>
            {isEditable && (
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} onClick={handleSeedStandardDays}>
                Add the standard days ({BOT_SEQUENCE_STANDARD_DAYS[sequenceType].map((day) => day.offsetDays).join(", ")})
              </Button>
            )}
          </div>
        ) : (
          ladder.map((step) => (
            <DayRow
              key={step.key}
              sequenceType={sequenceType}
              step={step}
              expanded={expandedKey === step.key}
              readOnly={!isEditable}
              numberEditable={!savedKeys.has(step.key)}
              onToggleExpanded={() => setExpandedKey(expandedKey === step.key ? null : step.key)}
              onEnabledChange={(enabled) => stage(updateDay(ladder, step.key, { enabled }))}
              onNumberChange={(offsetDays) => stage(updateDay(ladder, step.key, { offsetDays }))}
              onRemove={() => stage(removeDay(ladder, step.key))}
            >
              <DaySettings
                sequenceType={sequenceType}
                step={step}
                ladder={ladder}
                disabled={!isEditable}
                capability={capability}
                onChange={(patch) => stage(updateDay(ladder, step.key, patch))}
              />
            </DayRow>
          ))
        )}
        {isEditable && (
          <div className="border-t border-grey-100">
            <AddDayControl existingOffsets={ladder.map((step) => step.offsetDays)} onAdd={handleAdd} />
            {addError && <p className="body-xs px-4 pb-3 text-red-700">{addError}</p>}
          </div>
        )}
      </div>
    </section>
  );
}
